import React, { useState, useEffect, useRef } from "react";
import { Briefcase, AlertTriangle, Search, X } from "lucide-react";
import { Modal } from "./Modal";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

/**
 * MoveEquipmentToJobModal — "Add to Job" from the Equipment Register side.
 * Picks an EXISTING job (scoped to this equipment's own customer, searchable
 * since there can be thousands) and appends a new line to it for this
 * equipment record — no duplicate equipment doc is ever created. If the
 * equipment is already active on another job, offers a Transfer instead of
 * forking a duplicate line.
 */
export function MoveEquipmentToJobModal({ equipment, onClose, onSaved }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [job, setJob] = useState(null);       // picked job (full detail incl. job_status)
  const [activeWarn, setActiveWarn] = useState(null);
  const [techs, setTechs] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [form, setForm] = useState({
    date_received: new Date().toISOString().slice(0, 10),
    discipline: equipment.discipline || "",
    technician_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    api.get("/employees", { params: { role: "technician" } })
      .then((r) => setTechs(r.data)).catch(() => {});
    api.get("/disciplines").then((r) => setDisciplines(r.data || [])).catch(() => {});
    api.get(`/equipment/${equipment.id}/active-job-check`)
      .then((r) => setActiveWarn(r.data?.is_in_active_job ? r.data.active : null))
      .catch(() => {});
  }, [equipment.id]);

  useEffect(() => {
    if (!query || query.length < 1) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get("/jobs", {
          params: { customer_id: equipment.customer_id, search: query, limit: 15 },
        });
        setResults(r.data.items || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query, equipment.customer_id]);

  useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pickJob = async (j) => {
    if (activeWarn && activeWarn.job_id === j.id) {
      toast.error("This equipment is already on that job — pick a different one.");
      return;
    }
    setOpen(false);
    setQuery("");
    try {
      const r = await api.get(`/jobs/${j.id}`);
      setJob(r.data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const set = (patch) => setForm((s) => ({ ...s, ...patch }));

  const submit = async () => {
    if (!job) { toast.error("Pick a job first"); return; }
    setBusy(true);
    try {
      const payload = {
        description: equipment.description, manufacturer: equipment.manufacturer,
        part_number: equipment.part_number, serial_number: equipment.serial_number,
        asset_number: equipment.asset_number, issue_number: equipment.issue_number,
        calibration_period_days: equipment.calibration_period_days || null,
        equipment_id: equipment.id,
        discipline: form.discipline || null,
        technician_id: form.technician_id || null,
        date_received: form.date_received || null,
      };
      const r = await api.post(`/jobs/${job.id}/items`, payload);
      if (r.data?.duplicate_warning) {
        toast.warning(`Heads-up: this equipment was already on DN ${r.data.duplicate_warning.delivery_note_number} (${r.data.duplicate_warning.status})`);
      } else {
        toast.success(`Added to DN ${job.delivery_note_number || ""}`);
      }
      onSaved && onSaved(r.data);
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const transferInstead = async () => {
    if (!job || !activeWarn?.job_item_id) return;
    setTransferring(true);
    try {
      await api.post(`/job-items/${activeWarn.job_item_id}/transfer`, { target_job_id: job.id });
      toast.success(`Transferred to DN ${job.delivery_note_number || ""} — no duplicate line created`);
      onSaved && onSaved({});
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setTransferring(false); }
  };

  return (
    <Modal open onClose={onClose} icon={Briefcase}
      title="Add this equipment to a job"
      subtitle={`${equipment.description || ""} · ${equipment.customer_name || ""}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn gold" onClick={submit} disabled={busy || !job}
          data-testid="move-eq-submit">
          {busy ? "Adding…" : "Add to job"}
        </button>
      </>}>

      <div className="fld" style={{ marginBottom: 14, position: "relative" }} ref={wrapRef}>
        <label>Job (Delivery note #) <span className="req">*</span></label>
        {job ? (
          <div className="input" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
            data-testid="move-eq-job-selected">
            <span className="t-strong mono" style={{ fontSize: 13 }}>
              {job.delivery_note_number} <span className="t-mut" style={{ fontWeight: 400 }}>· {job.item_count || 0} line(s)</span>
            </span>
            <button type="button" className="icon-btn" style={{ width: 22, height: 22 }}
              onClick={() => setJob(null)} data-testid="move-eq-job-clear"><X size={13} /></button>
          </div>
        ) : (
          <>
            <div className="input" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Search size={13} color="var(--text-3)" />
              <input className="mono" style={{ border: "none", outline: "none", width: "100%", background: "none" }}
                placeholder="Search this customer's jobs by DN…" value={query}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)} data-testid="move-eq-job-search" />
            </div>
            {open && (query.length >= 1) && (
              <div className="card" style={{ position: "absolute", zIndex: 20, top: 62, left: 0, right: 0, maxHeight: 260, overflowY: "auto", padding: 4 }}>
                {searching && <div className="t-mut" style={{ fontSize: 12, padding: "8px 10px" }}>Searching…</div>}
                {!searching && results.length === 0 && (
                  <div className="t-mut" style={{ fontSize: 12, padding: "8px 10px" }}>No matching jobs for &quot;{query}&quot;.</div>
                )}
                {results.map((r) => (
                  <button key={r.id} type="button" className="nav-item"
                    style={{ color: "var(--text)", borderRadius: 6, width: "100%", textAlign: "left", padding: "8px 10px" }}
                    onClick={() => pickJob(r)} data-testid={`move-eq-job-opt-${r.id}`}>
                    <span className="mono t-strong" style={{ fontSize: 13 }}>{r.delivery_note_number}</span>
                    <span className="t-mut" style={{ fontSize: 11.5, marginLeft: 8 }}>{r.date_received || ""}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {job && job.job_status === "closed" && (
        <div className="card pad" data-testid="move-eq-closed-job-notice"
          style={{ marginBottom: 12, background: "var(--card-2,#f2f5fb)", border: "1px solid #cdd9ef",
                   display: "flex", gap: 10, alignItems: "flex-start" }}>
          <AlertTriangle size={16} style={{ color: "#3a5a9a", marginTop: 1 }} />
          <div style={{ fontSize: 12.5 }}>
            <div className="t-strong">This job is currently Closed</div>
            <div className="t-mut" style={{ marginTop: 2 }}>
              Adding this equipment will automatically reactivate the job.
            </div>
          </div>
        </div>
      )}

      {activeWarn && (
        <div className="card pad" data-testid="move-eq-active-warning"
          style={{ marginBottom: 14, background: "#fff8e6", border: "1px solid #e3d28a",
                   display: "flex", gap: 10, alignItems: "flex-start" }}>
          <AlertTriangle size={16} style={{ color: "#8a5a00", marginTop: 1 }} />
          <div style={{ fontSize: 12.5 }}>
            <div className="t-strong">Already in service</div>
            <div className="t-mut" style={{ marginTop: 2 }}>
              This equipment is currently active on DN <b className="mono">{activeWarn.delivery_note_number || "—"}</b>
              {activeWarn.technician_name && <> · with <b>{activeWarn.technician_name}</b></>}
              {activeWarn.status && <> · status <b>{activeWarn.status.replace(/_/g, " ")}</b></>}.
              {job ? " Move it here instead of creating a second line, or add anyway below if this is genuinely a separate physical item." : " Pick a job below, then choose to transfer it."}
            </div>
            {job && (
              <button type="button" className="btn ghost sm" style={{ marginTop: 8 }}
                onClick={transferInstead} disabled={transferring}
                data-testid="move-eq-transfer-btn">
                {transferring ? "Transferring…" : `Transfer to DN ${job.delivery_note_number} instead`}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="form-grid three">
        <div className="fld">
          <label>Date received</label>
          <input className="input" type="date" value={form.date_received}
            onChange={(e) => set({ date_received: e.target.value })}
            data-testid="move-eq-date-received" />
        </div>
        <div className="fld">
          <label>Discipline</label>
          <select className="input" value={form.discipline}
            onChange={(e) => set({ discipline: e.target.value })}
            data-testid="move-eq-discipline">
            <option value="">— select —</option>
            {disciplines.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
        </div>
        <div className="fld">
          <label>Technician</label>
          <select className="input" value={form.technician_id}
            onChange={(e) => set({ technician_id: e.target.value })}
            data-testid="move-eq-technician">
            <option value="">— unassigned —</option>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      </div>
    </Modal>
  );
}
